import Box from 'ext-ui/components/Box/Box'
import React, { ReactElement, useMemo } from 'react'
import { compileStackedBarPlan } from 'src/charting/plan/compileStackedBarPlan'
import VisxStackedBarChart, {
  formatCompactValueTrimmed,
} from 'src/charting/renderers/visx/VisxStackedBarChart'
import type { ChartDataRow, ChartSpec } from 'src/charting/spec/chartSpec.types'
import { ExtendCurrency } from 'src/enums'
import * as style from 'src/feature/CardOverviewSpendByCategory/cardOverviewSpendByCategory.style'
import {
  CARD_OVERVIEW_SPEND_BY_CATEGORY_DEMO,
  type SpendByCategoryDemoRow,
  type SpendByCategoryKey,
} from 'src/feature/CardOverviewSpendByCategory/cardOverviewSpendByCategoryDemoData'
import { useTranslate } from 'src/localization/Trns'
import { useNullSafeLocale } from 'src/localization/useNullSafeLocale'

/** Stands in for a category code on the residual bucket, which aggregates several. */
const NO_CATEGORY_CODE = '—'

export type CardOverviewSpendByCategoryProps = {
  /**
   * Defaults to the fabricated fixture — there is no per-card spend-by-category
   * endpoint to read. See `cardOverviewSpendByCategoryDemoData`. Overridable so
   * tests can drive the empty and single-bucket cases without stubbing modules.
   */
  rows?: SpendByCategoryDemoRow[]
  currency?: ExtendCurrency
  className?: string
}

/**
 * "Spend by category" on the card Overview tab — one bar per expense category,
 * ranked by spend.
 *
 * Lifted from the Reporting dashboard's `SpendByAccountingCategoryPanel`, but
 * only its rendering half. `SpendStackedBarPanel` itself is not reusable here:
 * it calls `useHistory` to route out of its gated states, which the layering
 * rule forbids in `src/feature/`, and its SaaS-tier and ERP-integration gates
 * describe an org-wide dashboard rather than one card. What carries over is the
 * part worth carrying — the declarative spec compiled by `compileStackedBarPlan`
 * into a `ChartPlan`, so the categorical color tokens, currency-cents axis
 * formatting and tooltips all come for free.
 *
 * Color encodes the SAME field as the x axis, which is ordinarily redundant —
 * position already separates the categories. It earns its place here because
 * the compiler's `getSemanticColorToken` recognises the key `"other"` and hands
 * it `charts_categoricalOther` grey: the residual bucket then reads as visually
 * apart from the real categories rather than as a seventh one. The design asks
 * for per-category color; this is why it is worth having.
 *
 * Presentational and routing-free.
 */
export default function CardOverviewSpendByCategory(
  props: CardOverviewSpendByCategoryProps,
): ReactElement {
  const {
    rows = CARD_OVERVIEW_SPEND_BY_CATEGORY_DEMO,
    currency = ExtendCurrency.Usd,
    className,
  } = props

  const translate = useTranslate()
  const locale = useNullSafeLocale()

  const {
    plan,
    topCategoryLabel,
    topCategoryCents,
    topCategoryColor,
    totalSpendCents,
  } = useMemo(() => {
    // One literal `translate` call per key so the localization gate sees every
    // label. A `translate(row.categoryKey)` lookup would type-check and then
    // ship untranslated.
    const labels: Record<SpendByCategoryKey, string> = {
      travel: translate('Travel & lodging'),
      software: translate('Software & SaaS'),
      advertising: translate('Advertising'),
      dining: translate('Dining'),
      entertainment: translate('Entertainment'),
      other: translate('Other'),
    }

    const chartRows: ChartDataRow[] = rows.map((row) => ({
      category: labels[row.categoryKey],
      // Rendered in the tooltip, so the residual bucket shows a dash rather
      // than an empty row.
      categoryCode: row.categoryCode ?? NO_CATEGORY_CODE,
      spendCents: row.spendCents,
    }))

    const spec: ChartSpec = {
      version: 1,
      mark: 'bar',
      title: translate('Spend by category'),
      description: translate('Spend grouped by expense category.'),
      ariaLabel: translate('Spend by category chart'),
      encoding: {
        x: {
          field: 'category',
          type: 'nominal',
          title: translate('Category'),
        },
        // Same field as `x` — see the note above the component. The explicit
        // domain pins each category to a token by POSITION, so a category
        // keeps its color even when the rows are reordered or one drops out.
        color: {
          field: 'category',
          type: 'nominal',
          title: translate('Category'),
          domain: rows.map((row) => labels[row.categoryKey]),
        },
        y: {
          field: 'spendCents',
          type: 'quantitative',
          valueFormat: 'currencyCents',
          // Doubles as the series label in the tooltip — with no color
          // encoding the compiler names the lone series after this.
          title: translate('Spend'),
        },
        tooltip: [
          {
            field: 'categoryCode',
            type: 'nominal',
            title: translate('Category code'),
          },
          {
            field: 'spendCents',
            type: 'quantitative',
            valueFormat: 'currencyCents',
            title: translate('Spend'),
          },
        ],
      },
    }

    // The residual bucket is excluded from the headline: it is an aggregate of
    // several codes, so calling it the top category would name something that
    // is not a category. Same reasoning as `isUnattributedSeriesKey` on the
    // Reporting dashboard.
    const top = rows
      .filter((row) => row.categoryCode !== null)
      .reduce<
        SpendByCategoryDemoRow | undefined
      >((best, row) => (best === undefined || row.spendCents > best.spendCents ? row : best), undefined)

    const compiled = compileStackedBarPlan(spec, chartRows)
    const topLabel = top !== undefined ? labels[top.categoryKey] : undefined

    return {
      plan: compiled,
      topCategoryLabel: topLabel,
      topCategoryCents: top?.spendCents,
      // Read back off the compiled plan rather than recomputed: the swatch
      // beside the headline has to be the token the top bar actually got, and
      // the compiler is what assigns it.
      topCategoryColor: compiled.series.find(
        (series) => series.key === topLabel,
      )?.color,
      totalSpendCents: rows.reduce((sum, row) => sum + row.spendCents, 0),
    }
  }, [rows, translate])

  // The SAME formatter the chart uses for its bar and axis labels under
  // `compactValueLabels`, so the headline can never disagree with the bar it
  // names. Deliberately not the app's `useCurrencyFormatter` — see the note on
  // `formatCompactValueTrimmed` for why it cannot produce "$980".
  const formatMoney = (cents: number): string =>
    formatCompactValueTrimmed(cents, 'currencyCents', currency, locale)

  // Zero total, not just an empty array: rows that are all zero compile to a
  // chart with no bars, which reads as broken rather than empty.
  const isEmpty = totalSpendCents === 0

  return (
    <Box className={className} data-testid="cardOverviewSpendByCategory">
      <div css={style.panel}>
        <h2 css={style.title}>{translate('Spend by category')}</h2>

        {isEmpty ? (
          <p
            css={style.emptyMessage}
            data-testid="cardOverviewSpendByCategory-empty"
          >
            {translate('No spend to show yet.')}
          </p>
        ) : (
          <>
            <div css={style.statsRow}>
              {topCategoryLabel !== undefined &&
                topCategoryCents !== undefined && (
                  <div
                    css={style.stat}
                    data-testid="cardOverviewSpendByCategory-topCategory"
                  >
                    <span css={style.statLabel}>
                      {translate('Top category')}
                    </span>
                    <span css={style.topCategoryValue}>
                      {/*
                        Decorative only — the category is named in the very next
                        breath, so the swatch adds no information a screen reader
                        would miss. Colour is never the sole carrier here.
                      */}
                      <span
                        css={style.swatch}
                        style={{ background: topCategoryColor }}
                        aria-hidden="true"
                        data-testid="cardOverviewSpendByCategory-swatch"
                      />
                      {`${topCategoryLabel} · ${formatMoney(topCategoryCents)}`}
                    </span>
                  </div>
                )}
              <div
                css={style.stat}
                data-testid="cardOverviewSpendByCategory-total"
              >
                <span css={style.statLabel}>{translate('Total spend')}</span>
                <span css={style.statValue}>
                  {formatMoney(totalSpendCents)}
                </span>
              </div>
            </div>

            <div
              css={style.chartBody}
              data-testid="cardOverviewSpendByCategory-chart"
            >
              <VisxStackedBarChart
                plan={plan}
                currency={currency}
                locale={locale}
                showLegend={false}
                showGridlines
                compactValueLabels
                horizontalXLabels
              />
            </div>
          </>
        )}
      </div>
    </Box>
  )
}
